import React, { useState } from 'react';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { PencilSimple, Check, X } from '@phosphor-icons/react';
import { toast } from 'sonner';

/**
 * Inline-editable number field (z.B. Kundennummer, Mietvertrag-Nr).
 * Props:
 *   - value (string): aktueller Wert
 *   - label (string): Anzeige-Label
 *   - onSave (async fn(newValue)): wird beim Speichern aufgerufen; muss Promise zurückgeben
 *   - testId (string)
 *   - small (bool): kompakte Darstellung
 */
const EditableNumber = ({ value, label, onSave, testId, small = false }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || '');
  const [saving, setSaving] = useState(false);

  const startEdit = () => {
    setDraft(value || '');
    setEditing(true);
  };

  const cancel = () => {
    setEditing(false);
    setDraft(value || '');
  };

  const save = async () => {
    const newVal = (draft || '').trim();
    if (!newVal) {
      toast.error('Nummer darf nicht leer sein');
      return;
    }
    if (newVal === value) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      await onSave(newVal);
      setEditing(false);
      toast.success(`${label || 'Nummer'} aktualisiert`);
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'Fehler beim Speichern';
      toast.error(detail);
    } finally {
      setSaving(false);
    }
  };

  if (editing) {
    return (
      <span className="inline-flex items-center gap-1" data-testid={testId ? `${testId}-edit` : undefined}>
        <Input
          autoFocus
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') save();
            if (e.key === 'Escape') cancel();
          }}
          className={small ? 'h-7 w-32 text-xs' : 'h-8 w-44'}
          data-testid={testId ? `${testId}-input` : undefined}
        />
        <Button type="button" size="sm" variant="ghost" onClick={save} disabled={saving}
                className="p-1 h-7 w-7" data-testid={testId ? `${testId}-save` : undefined}>
          <Check weight="bold" size={14} className="text-[#009951]" />
        </Button>
        <Button type="button" size="sm" variant="ghost" onClick={cancel} disabled={saving}
                className="p-1 h-7 w-7">
          <X weight="bold" size={14} className="text-[#E60000]" />
        </Button>
      </span>
    );
  }

  return (
    <span className="inline-flex items-center gap-1 group" data-testid={testId}>
      <span className={small ? 'font-mono text-xs' : 'font-mono'}>{value || '—'}</span>
      <button
        type="button"
        onClick={startEdit}
        className="p-1 opacity-50 group-hover:opacity-100 transition-opacity hover:text-[#E60000]"
        title="Nummer bearbeiten"
        data-testid={testId ? `${testId}-edit-btn` : undefined}
      >
        <PencilSimple weight="bold" size={small ? 11 : 13} />
      </button>
    </span>
  );
};

export default EditableNumber;
